import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AppConfigService } from './app-config.service';
import { ErrorHandlerService } from './error-handler.service';
import { FieldLayoutViewModel, FieldViewModel } from '../models/field/field-layout-view-model.model';
import { QuickLinkUpdate } from '../components/review/quick-links/quick-link-update';
import { LayoutQuickLink } from '../components/review/quick-links/layout-quick-link';
import { FieldLayoutDefault } from '../models/field/field-layout-default.model';

@Injectable({
  providedIn: 'root'
})
export class FieldLayoutViewModelService implements QuickLinkUpdate {

  constructor(private http: HttpClient, private errorHandler: ErrorHandlerService, private appConfigService: AppConfigService) { }

  getLayouts(): Observable<FieldLayoutViewModel[]> {
    return this.http.get<FieldLayoutViewModel[]>(`${this.appConfigService.getConfig().webApi}/api2/field-layout-view-models`)
      .pipe(catchError(this.errorHandler.handleError<FieldLayoutViewModel[]>(`get all layouts`)));
  }

  /** Retrieves the current user's active layout for the current case. */
  getActiveLayout(): Observable<FieldLayoutViewModel> {
    return this.http.get<FieldLayoutViewModel>(`${this.appConfigService.getConfig().webApi2}/api2/activelayout`)
      .pipe(catchError(this.errorHandler.handleError<FieldLayoutViewModel>(`get active layout`)));
  }

  getAvailableFieldViewModel(): Observable<FieldViewModel[]> {
    return this.http.get<FieldViewModel[]>(`${this.appConfigService.getConfig().webApi}/api2/field-view-models`)
      .pipe(catchError(this.errorHandler.handleError<FieldViewModel[]>(`get available fields`)));
  }

  getDefaultWithheldLogLayout(): Observable<FieldLayoutViewModel> {
	return this.http.get<FieldLayoutViewModel>(`${this.appConfigService.getConfig().webApi}/api2/field-layout-view-models/default-withheld-log-layout`)
		.pipe(catchError(this.errorHandler.handleError<FieldLayoutViewModel>(`get default withheld log layout`)));
  }

  getDefaultFieldIds(): Observable<string[]> {
    return this.http.get<string[]>(`${this.appConfigService.getConfig().webApi}/api2/field-view-models/default-field-ids`)
      .pipe(catchError(this.errorHandler.handleError<string[]>(`get available fields`)));
  }

  createFieldLayout(fields: FieldViewModel[]): Observable<FieldLayoutViewModel> {
    return this.http.post<FieldLayoutViewModel>(`${this.appConfigService.getConfig().webApi}/api2/field-layout-view-models`, fields)
      .pipe(catchError(this.errorHandler.handleError<FieldLayoutViewModel>(`post field layout view model`)));
  }

  updateFieldLayout(data: FieldLayoutViewModel): Observable<FieldLayoutViewModel> {
    return this.http.put<FieldLayoutViewModel>(`${this.appConfigService.getConfig().webApi}/api2/field-layout-view-models/${data.fieldLayoutId}`, data)
      .pipe(catchError(this.errorHandler.handleError<FieldLayoutViewModel>(`post field layout view model`)));
  }

  updateActiveLayout(fieldLayoutId: string): Observable<any> {
    return this.http.put<boolean>(`${this.appConfigService.getConfig().webApi2}/api2/activelayout/${fieldLayoutId}`, null)
      .pipe(catchError(this.errorHandler.handleError<boolean>(`put active layout`)));
  }

  updateLink(quickLink: LayoutQuickLink): Observable<boolean> {
    const layout = quickLink.getLayout();
    if (quickLink.lifeCycle === 0) {
      // Not deleted, just update
      return this.http.put<boolean>(`${this.appConfigService.getConfig().webApi}/api2/field-layout-view-models/${layout.fieldLayoutId}`, layout)
        .pipe(catchError(this.errorHandler.handleError<boolean>(`put field layout view model`)));
    } else {
      return this.deleteFieldLayoutModel(layout.fieldLayoutId);
    }
  }

  deleteFieldLayoutModel(fieldLayoutId: string): Observable<any> {
    return this.http.delete(`${this.appConfigService.getConfig().webApi}/api2/field-layout-view-models/${fieldLayoutId}`)
      .pipe(catchError(this.errorHandler.handleError<FieldLayoutViewModel>(`delete field layout model for fieldLayoutId=${fieldLayoutId}`)));
  }

  public getDefaultLayouts(): Observable<FieldLayoutDefault[]> {
    return this.http.get<FieldLayoutDefault[]>(`${this.appConfigService.getConfig().webApi}/api2/FieldLayoutDefaults`)
      .pipe(catchError(this.errorHandler.handleError<FieldLayoutDefault[]>(`get available field layout defaults`)));
  }

  public updateDefaultLayout(layoutDefault: FieldLayoutDefault): Observable<FieldLayoutDefault> {
    return this.http.post<FieldLayoutDefault>(`${this.appConfigService.getConfig().webApi}/api2/FieldLayoutDefaults`, layoutDefault)
      .pipe(catchError(this.errorHandler.handleError<FieldLayoutDefault>(`post default layout`)));
  }

  public removeDefault(layoutDefaultId: string): Observable<boolean> {
    return this.http.delete<boolean>(`${this.appConfigService.getConfig().webApi}/api2/FieldLayoutDefaults/${layoutDefaultId}`)
      .pipe(catchError(this.errorHandler.handleError<boolean>(`delete default layout`)));
  }
}
